import type { FC, ReactNode } from 'react'
import type { CrashState } from '@/types'

import { useCallback, useEffect, useRef, useState } from 'react'
import { api, errorMessage, RequestError } from '@/lib/client'
import { CRASH_GROWTH, CRASH_MIN_AUTO, crashMultiplierAt, crashTimeFor, payoutFor } from '@/lib/games'
import { formatShort } from '@/lib/money'
import { play } from '@/lib/sounds'
import { cn } from '@/lib/utils'
import { useSession } from '@/hooks/use-session'
import { Button } from '@/components/generals/button'
import { inputClass } from '@/components/generals/fields'
import { Arena, BetInput, GameLayout, Panel, useStake } from '@/components/games/game-ui'
import { usePlay } from '@/components/games/use-play'
import { PixelSprite } from '@/components/pixel/pixel-sprite'
import { EXPLOSION, ROCKET, ROCKET_FLAME } from '@/components/pixel/sprites'

type Phase = 'idle' | 'starting' | 'running' | 'cashing' | 'won' | 'crashed'

type CrashResponse = {
    state: CrashState
    balance: number | null
}

type Outcome = {
    won: boolean
    multiplier: number
    crash: number | null
    payout: number
}

const SAMPLES = 48

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))

const CrashGame: FC = (): ReactNode => {
    const { user, maintenance, setBalance } = useSession()
    const { canBet, toast } = usePlay()
    const stake = useStake()
    const [autoText, setAutoText] = useState('')
    const [phase, setPhase] = useState<Phase>('idle')
    const [bet, setBet] = useState<{ id: string, amount: number, auto: number | null } | null>(null)
    const [elapsed, setElapsed] = useState(0)
    const [outcome, setOutcome] = useState<Outcome | null>(null)
    const [history, setHistory] = useState<number[]>([])
    const [box, setBox] = useState({ width: 640, height: 320 })
    const startedAt = useRef(0)
    const following = useRef<string | null>(null)
    const plotRef = useRef<HTMLDivElement>(null)

    const flying = phase === 'running' || phase === 'cashing'

    useEffect(() => {
        const plot = plotRef.current

        if (!plot) return

        const observer = new ResizeObserver(([entry]) => setBox({ width: entry.contentRect.width, height: entry.contentRect.height }))

        observer.observe(plot)

        return () => observer.disconnect()
    }, [])

    // Drive the rocket from the moment the server launched it.
    useEffect(() => {
        if (!flying) return

        let frame = 0

        const tick = () => {
            setElapsed(performance.now() - startedAt.current)
            frame = requestAnimationFrame(tick)
        }

        frame = requestAnimationFrame(tick)

        return () => cancelAnimationFrame(frame)
    }, [flying])

    // Stop following a round when leaving the page.
    useEffect(() => () => {
        following.current = null
    }, [])

    const finish = useCallback((state: CrashState, balance: number | null) => {
        if (following.current !== state.id) return

        following.current = null

        const won = state.status === 'won'
        const multiplier = won ? state.multiplier : (state.crash ?? 1)

        setOutcome({ won, multiplier, crash: state.crash, payout: state.payout })
        setElapsed(crashTimeFor(multiplier))
        setPhase(won ? 'won' : 'crashed')

        if (state.crash) setHistory((current) => [state.crash as number, ...current].slice(0, 12))
        if (balance !== null) setBalance(balance)

        play(won ? 'cashout' : 'boom')
    }, [setBalance])

    // Holds a request open on the server until the rocket blows or the auto cash-out hits.
    const follow = useCallback(async (id: string) => {
        while (following.current === id) {
            const asked = Date.now()

            try {
                const data = await api<CrashResponse>('/api/games/crash', { body: { action: 'wait', betId: id } })

                if (data.state.status !== 'active') {
                    finish(data.state, data.balance)

                    return
                }

                // Never ask again faster than twice a second, even if the server answered early.
                const spent = Date.now() - asked

                if (spent < 500) await sleep(500 - spent)
            }
            catch (caught) {
                if (following.current !== id) return

                if (caught instanceof RequestError && caught.status >= 400 && caught.status < 500) {
                    following.current = null
                    setPhase('idle')
                    toast(errorMessage(caught), 'error')

                    return
                }

                await sleep(1_200)
            }
        }
    }, [finish, toast])

    const launch = useCallback((state: CrashState) => {
        startedAt.current = performance.now() - state.elapsedMs
        following.current = state.id
        setBet({ id: state.id, amount: state.amount, auto: state.auto })
        setOutcome(null)
        setElapsed(state.elapsedMs)
        setPhase('running')
        void follow(state.id)
    }, [follow])

    // Pick up a rocket that is still flying (after a refresh) and load recent results.
    const userId = user?.id

    useEffect(() => {
        if (!userId) return

        let cancelled = false

        api<{ state: CrashState | null, history: number[] }>('/api/games/crash')
            .then((data) => {
                if (cancelled) return

                setHistory(data.history)

                if (data.state && data.state.status === 'active' && !following.current) launch(data.state)
            })
            .catch(() => undefined)

        return () => {
            cancelled = true
        }
    }, [userId, launch])

    const start = async () => {
        if (phase === 'starting' || flying || !canBet(stake.amount)) return

        const auto = autoText.trim() ? Number(autoText.replace(/[x×\s]/gi, '')) : null

        if (auto !== null && (!Number.isFinite(auto) || auto < CRASH_MIN_AUTO)) {
            toast(`Auto cash-out must be at least ${CRASH_MIN_AUTO.toFixed(2)}×.`, 'error')

            return
        }

        setPhase('starting')

        try {
            const data = await api<CrashResponse>('/api/games/crash', { body: { action: 'start', amount: stake.amount, auto } })

            if (data.balance !== null) setBalance(data.balance)

            play('launch')
            launch(data.state)
        }
        catch (caught) {
            setPhase('idle')
            toast(errorMessage(caught), 'error')
        }
    }

    const cashOut = async () => {
        if (phase !== 'running' || !bet) return

        setPhase('cashing')

        try {
            const data = await api<CrashResponse>('/api/games/crash', { body: { action: 'cashout', betId: bet.id } })

            finish(data.state, data.balance)
        }
        catch (caught) {
            if (following.current === bet.id) setPhase('running')

            toast(errorMessage(caught), 'error')
        }
    }

    // Chart geometry, in percent of the plot box.
    const seconds = Math.max(0, elapsed / 1000)
    const live = crashMultiplierAt(elapsed)
    const shown = outcome ? outcome.multiplier : flying ? live : 1
    const xMax = Math.max(8, seconds * 1.15)
    const yMax = Math.max(2, shown * 1.2)
    const toX = (time: number) => (time / xMax) * 100
    const toY = (value: number) => 100 - ((value - 1) / (yMax - 1)) * 100
    const hasFlight = phase !== 'idle' && phase !== 'starting'

    const points = hasFlight
        ? Array.from({ length: SAMPLES + 1 }, (_, i) => {
            const time = (seconds * i) / SAMPLES

            return `${toX(time).toFixed(2)},${toY(crashMultiplierAt(time * 1000)).toFixed(2)}`
        }).join(' ')
        : ''

    const tipX = hasFlight ? toX(seconds) : 0
    const tipY = hasFlight ? toY(shown) : 100
    const slopeX = (100 / xMax) * (box.width / 100)
    const slopeY = -((100 * CRASH_GROWTH * shown) / (yMax - 1)) * (box.height / 100)
    const angle = hasFlight ? (Math.atan2(slopeY, slopeX) * 180) / Math.PI + 90 : 45

    const autoValue = bet?.auto ?? (autoText.trim() ? Number(autoText) : null)
    const showAuto = autoValue !== null && Number.isFinite(autoValue) && autoValue >= CRASH_MIN_AUTO && autoValue <= yMax

    const yTicks = [0, 1, 2, 3, 4].map((i) => 1 + (i * (yMax - 1)) / 4)
    const xTicks = [0, 1, 2, 3, 4].map((i) => (i * xMax) / 4)

    let status: ReactNode = 'Waiting for your bet'

    if (phase === 'starting') status = 'Launching…'
    if (phase === 'running') status = bet?.auto ? `Auto cash-out at ${bet.auto.toFixed(2)}×` : 'Cash out before it blows'
    if (phase === 'cashing') status = 'Cashing out…'
    if (phase === 'won' && outcome) {
        status = (
            <span className='text-success'>
                You won {formatShort(outcome.payout)}
                {outcome.crash ? <span className='text-muted-foreground'>{` · it blew at ${outcome.crash.toFixed(2)}×`}</span> : null}
            </span>
        )
    }
    if (phase === 'crashed' && outcome) status = <span className='text-danger'>Boom. It blew at {outcome.multiplier.toFixed(2)}×</span>

    const cashValue = bet ? payoutFor(bet.amount, Math.floor(live * 100) / 100) : 0

    const panel = (
        <Panel>
            <BetInput value={stake.text} onChange={stake.setText} disabled={flying || phase === 'starting'} />

            <div>
                <label htmlFor='crash-auto' className='mb-1.5 block text-sm font-semibold text-foreground/90'>
                    Auto cash-out (optional)
                </label>

                <div className='relative'>
                    <input
                        id='crash-auto'
                        value={autoText}
                        onChange={(event) => setAutoText(event.target.value)}
                        disabled={flying || phase === 'starting'}
                        inputMode='decimal'
                        autoComplete='off'
                        placeholder='e.g. 2.00'
                        className={cn(inputClass, 'h-12 pr-9')}
                    />
                    <span className='pointer-events-none absolute inset-y-0 right-3 flex items-center text-muted-foreground' aria-hidden='true'>×</span>
                </div>
            </div>

            {flying ? (
                <Button variant='success' size='lg' className='w-full' onClick={cashOut} loading={phase === 'cashing'}>
                    Cash out {formatShort(cashValue)}
                </Button>
            ) : (
                <Button size='lg' className='w-full' onClick={start} loading={phase === 'starting'} disabled={maintenance}>
                    {maintenance ? 'Paused for maintenance' : 'Place bet'}
                </Button>
            )}
        </Panel>
    )

    const arena = (
        <Arena className='flex flex-col'>
            <div className='relative flex-1 pt-5 pr-5 pb-9 pl-12'>
                <div ref={plotRef} className='relative h-[280px] sm:h-[340px]'>
                    {yTicks.map((tick) => (
                        <div key={`y-${tick}`} className='absolute inset-x-0' style={{ top: `${toY(tick)}%` }} aria-hidden='true'>
                            <div className='border-t border-white/[0.06]' />
                            <span className='absolute -left-11 -translate-y-1/2 text-[11px] text-muted-foreground'>
                                {tick >= 10 ? tick.toFixed(0) : tick.toFixed(1)}×
                            </span>
                        </div>
                    ))}

                    {xTicks.map((tick) => (
                        <span key={`x-${tick}`} className='absolute -bottom-7 -translate-x-1/2 text-[11px] text-muted-foreground' style={{ left: `${toX(tick)}%` }} aria-hidden='true'>
                            {Math.round(tick)}s
                        </span>
                    ))}

                    {showAuto && autoValue !== null && (
                        <div className='absolute inset-x-0 border-t border-dashed border-gold/60' style={{ top: `${toY(autoValue)}%` }} aria-hidden='true'>
                            <span className='absolute right-0 -translate-y-full pb-0.5 text-[11px] font-semibold text-gold'>Auto {autoValue.toFixed(2)}×</span>
                        </div>
                    )}

                    <svg viewBox='0 0 100 100' preserveAspectRatio='none' className='absolute inset-0 size-full overflow-visible' aria-hidden='true'>
                        <defs>
                            <linearGradient id='crash-fill' x1='0' y1='0' x2='0' y2='1'>
                                <stop offset='0%' stopColor='#ff8a1c' stopOpacity='0.35' />
                                <stop offset='100%' stopColor='#ff8a1c' stopOpacity='0' />
                            </linearGradient>
                        </defs>

                        {points && (
                            <>
                                <polygon points={`0,100 ${points} ${tipX.toFixed(2)},100`} fill='url(#crash-fill)' />
                                <polyline points={points} fill='none' stroke={phase === 'crashed' ? '#ff5a4a' : '#ffb44a'} strokeWidth='3' strokeLinejoin='round' strokeLinecap='round' vectorEffect='non-scaling-stroke' />
                            </>
                        )}
                    </svg>

                    <div className='pointer-events-none absolute' style={{ left: `${tipX}%`, top: `${tipY}%` }} aria-hidden='true'>
                        {phase === 'crashed' ? (
                            <PixelSprite sprite={EXPLOSION} className='df-pop w-16 -translate-x-1/2 -translate-y-1/2' />
                        ) : (
                            <div className='w-5 sm:w-6' style={{ transform: `translate(-50%, -50%) rotate(${angle}deg)` }}>
                                <PixelSprite sprite={ROCKET} className='w-full' />
                                {flying && <PixelSprite sprite={ROCKET_FLAME} className='w-full animate-pulse' />}
                            </div>
                        )}
                    </div>
                </div>

                <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center pb-10 text-center'>
                    <p
                        className={cn(
                            'font-heading text-6xl leading-none font-bold tabular-nums drop-shadow-[0_4px_0_rgba(0,0,0,0.45)] sm:text-[5.5rem]',
                            phase === 'won' && 'text-success',
                            phase === 'crashed' && 'text-danger df-shake'
                        )}
                        aria-hidden='true'
                    >
                        {shown.toFixed(2)}×
                    </p>

                    <p className='mt-3 px-4 text-base font-semibold text-foreground/85 sm:text-lg' aria-live='polite'>
                        {status}
                    </p>
                </div>
            </div>

            <div className='flex min-h-11 items-center gap-2 overflow-x-auto border-t border-border px-4 py-2 no-scrollbar'>
                <span className='shrink-0 text-xs text-muted-foreground'>{history.length ? 'Your last rockets' : 'Your last rockets show up here'}</span>

                {history.map((point, index) => (
                    <span
                        key={`${point}-${index}`}
                        className={cn('shrink-0 rounded-md border px-2 py-0.5 text-xs font-bold tabular-nums', point >= 2 ? 'border-success/40 bg-success/10 text-success' : 'border-border bg-secondary text-muted-foreground')}
                    >
                        {point.toFixed(2)}×
                    </span>
                ))}
            </div>
        </Arena>
    )

    return <GameLayout panel={panel} arena={arena} />
}

export default CrashGame
